import { FinanceMilestoneWorkbookImportResponse, FinanceMilestoneWorkbookPostImport } from '@/api/dtos';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { OnlinePatientManagementContext } from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { DateTime } from 'luxon';
import { FC, useContext, useState } from 'react';
import { useSWRConfig } from 'swr';

const formatDate = (value?: string) => value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : '—';

const FinanceMilestonesImportButton: FC = () => {
    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);
    const { permissions: [canAdminister], loading: permissionsLoading } = usePermissionsByIds([Permission.OpmsAdminister]);
    const { mutate } = useSWRConfig();
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState<File>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [results, setResults] = useState<FinanceMilestoneWorkbookImportResponse>();

    const upload = async () => {
        if (!file || loading) return;
        setLoading(true);
        setError(undefined);
        setResults(undefined);
        try {
            const data = new FormData();
            data.append('file', file);
            const api = await client.apiForm(new FinanceMilestoneWorkbookPostImport(), data, undefined, 'post');
            if (!api.succeeded || !api.response) {
                setError(api.error?.message ?? 'The report could not be imported.');
                return;
            }
            setResults(api.response);
            if (api.response.changes?.length) {
                try {
                    await mutate(key => typeof key === 'string' && (
                        key.startsWith('SwrClient:PathologyTrackingForm') ||
                        key.startsWith('SwrClient:PathologyTrackingEventForm') ||
                        key.startsWith('SwrClient:FormStates')
                    ), undefined, { revalidate: true });
                } catch {
                    setError('The import completed, but the form view could not be refreshed. Reload before editing.');
                }
            }
        } catch (exception) {
            setError(exception instanceof Error ? exception.message : 'The report could not be imported.');
        } finally {
            setLoading(false);
        }
    };

    if (permissionsLoading || !canAdminister) return null;

    return (
        <>
            <Button 
                variant="contained" 
                onClick={() => { setOpen(true); setFile(undefined); setResults(undefined); setError(undefined); }}
                disabled={loading}
            >
                Import Finance Milestones
            </Button>
            <Dialog open={open} onClose={() => { if (!loading) setOpen(false); }} maxWidth="md">
                <DialogTitle>Import Finance Milestones</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Select a Unicancer report. Dates will add or update visit milestones for all patients in the file.
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Blank cells leave existing entries unchanged.
                    </Typography>
                    <Button component="label" variant="outlined" disabled={loading}>
                        {
                            !file ?
                                'Upload' : 
                                file.name
                        }
                        
                        <input type="file" accept=".xls,.xlsx" hidden onChange={event => {
                            setFile(event.target.files?.[0]);
                            setResults(undefined);
                            setError(undefined);
                        }} />
                    </Button>

                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    {results && <Box sx={{ mt: 2 }} aria-live="polite">
                        {results.skippedPatients?.map((skip, index) => <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                            {skip.studyNumber || 'Missing Pt ID'} (row {skip.rows?.join(', ')}): {skip.reason}
                        </Alert>)}

                        {results.warnings?.map((warning, index) => <Alert key={index} severity="info" sx={{ mb: 1 }}>{warning}</Alert>)}

                        <Alert severity={results.skippedPatients?.length ? 'warning' : 'success'}>
                            {results.changes?.filter(change => change.added).length ?? 0} added,{' '}
                            {results.changes?.filter(change => !change.added).length ?? 0} updated,{' '}
                            {results.unchangedCount} unchanged,{' '}
                            {results.skippedPatients?.length ?? 0} patients skipped.
                        </Alert>

                        {!!results.changes?.length && <Table size="small" aria-label="Imported milestones">
                            <TableHead><TableRow>
                                <TableCell>Participant</TableCell><TableCell>Timepoint</TableCell>
                                <TableCell>Previous date</TableCell><TableCell>Imported date</TableCell><TableCell>Result</TableCell>
                            </TableRow></TableHead>
                            <TableBody>{results.changes.map((change, index) => <TableRow key={index}>
                                <TableCell>{change.studyNumber}</TableCell>
                                <TableCell>{change.timePointName}</TableCell>
                                <TableCell>{formatDate(change.previousDate)}</TableCell>
                                <TableCell>{formatDate(change.date)}</TableCell>
                                <TableCell>{change.added ? 'Added' : 'Updated'}</TableCell>
                            </TableRow>)}</TableBody>
                        </Table>}
                    </Box>}
                </DialogContent>
                <DialogActions>
                    <Button disabled={loading} onClick={() => setOpen(false)}>Close</Button>
                    <Button variant="contained" disabled={!file || loading} onClick={() => void upload()}>
                        {
                            loading ? 
                                'Importing' :
                                'Import'
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FinanceMilestonesImportButton;
